import { memo, useEffect, useState } from 'react'
import { clsx } from 'clsx'

import css from './ThemeToggle.module.scss'

import { type ResolvedTheme, resolveTheme, type ThemePreference } from '@/lib/store/useThemeStore'

import MoonIcon from '@/assets/svgs/moon.svg?react'
import SunIcon from '@/assets/svgs/sun.svg?react'
import SystemThemeDarkIcon from '@/assets/svgs/system-theme-dark.svg?react'
import SystemThemeLightIcon from '@/assets/svgs/system-theme-light.svg?react'

const CYCLE: Record<ThemePreference, ThemePreference> = {
  light: 'dark',
  dark: 'system',
  system: 'light'
}

const LABELS: Record<ThemePreference, string> = {
  light: 'Light theme',
  dark: 'Dark theme',
  system: 'System theme'
}

export interface ViewProps {
  className?: string
  preference: ThemePreference
  onPreferenceChange: (preference: ThemePreference) => void
}

const View = memo(function ThemeToggleView({ className, preference, onPreferenceChange }: ViewProps) {
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() => resolveTheme('system'))

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => setSystemTheme(media.matches ? 'dark' : 'light')
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])

  const SystemIcon = systemTheme === 'dark' ? SystemThemeDarkIcon : SystemThemeLightIcon
  const Icon = preference === 'light' ? SunIcon : preference === 'dark' ? MoonIcon : SystemIcon
  const label = LABELS[preference]
  const nextLabel = LABELS[CYCLE[preference]].toLowerCase()

  return (
    <button
      type="button"
      className={clsx('ThemeToggle', css.root, className)}
      onClick={() => onPreferenceChange(CYCLE[preference])}
      aria-label={`${label} active. Switch to ${nextLabel}`}
    >
      <Icon aria-hidden="true" />
      <span className={css.tooltip}>{label}</span>
    </button>
  )
})

View.displayName = 'ThemeToggle_View'

export default View
